<!--
  GarmentPreview.vue -- on-garment placement preview at true relative scale.

  Store Decorations redesign, pebble 3b (ADR 0065).
  Card: https://trello.com/c/oSOHJfg0

  Renders ONE placement: the product's garment silhouette (via the existing
  GarmentSilhouette component) with the decoration composited into its zone at
  TRUE RELATIVE SCALE -- the mark's on-screen size is its real inches relative
  to the zone's real printable inches, so an oversized decoration visibly
  overflows. Every preview carries the verdict pill
  (PlacementCompatibilityIndicator), unchanged.

  Four render states (ADR 0065 + Locked Decision 15a):
    * TRUE       -- the silhouette offers the zone AND we have printable inches:
                    full silhouette + scaled overlay + pill.
    * ROUGH      -- generic silhouette OR no authored hotspot for this
                    (silhouette, zone): a rough preview (silhouette + centered
                    schematic mark) + the EXACT pill. Jake's
                    locked best-effort fallback -- the verdict is arithmetic on
                    inches, not the drawing, so it stays exact. The parent LOGS
                    which placements land here (coverage transparency).
    * BLOCKED    -- the silhouette does NOT offer the zone at all (a tee has no
                    cap crown): a "zone not available on this garment" state, not
                    a bare garment or a fake verdict.
    * NO-DIMS    -- we cannot resolve the zone's printable inches: rough preview
                    with a neutral pill (verdict blocked). Rare; treated like
                    ROUGH for coverage logging.

  The component computes its OWN preview quality via `previewKind` and emits it
  on mount (`preview-kind`) so the parent can log a loud coverage summary.

  Verdict math is a faithful frontend mirror of the backend compute_verdict
  (placementPreviewGeometry.computePlacementVerdict); the pill component and its
  copy/colors are reused unchanged.

  data-testid prefix: garment-preview-*
-->
<template>
  <div class="garment-preview" :data-testid="`garment-preview-${placementId}`">
    <div class="garment-preview__stage" data-testid="garment-preview-stage">
      <!-- ADR 0065 AMENDMENT: the PRIMARY layer is a real Artifi live render
           (garment photo for this SKU+operator-color with the decoration
           composited). GarmentRenderLayer owns the six never-unbounded-3P
           controls and reports has-render; the schematic below is the v-else
           FALLBACK (retained, never deleted). Mounted only for a SanMar-
           eligible product with a resolvable zone + color. -->
      <div
        v-if="canRealRender"
        class="garment-preview__render"
        :class="{ 'garment-preview__render--faded-in': hasRealRender }"
        :data-testid="`garment-preview-render-${placementId}`"
      >
        <garment-render-layer
          :supplier-sku="supplierSku"
          :supplier-name="supplierName"
          :supplier-key="supplierKey"
          :supplier-id="supplierId"
          :color-name="effectiveColor"
          :zone-slug="zoneSlug"
          :artwork-file-id="renderArtworkFileId"
          :placement-id="placementId"
          @has-render="onHasRender"
          @loading="onLoading"
          @warnings="onWarnings"
        />
      </div>

      <!-- The silhouette fills the stage; overlays are absolutely positioned
           over it in viewBox-percentage coordinates so they track the
           silhouette's own contain-fit. Hidden when a real render is on
           screen (design-philosophy principle 4: one value picks the layer). -->
      <garment-silhouette
        v-if="showSchematic"
        :silhouette="resolvedSilhouette"
        :view="face"
        :color="silhouetteColor"
        class="garment-preview__silhouette"
      />

      <!-- The decoration overlay at true relative scale (TRUE + ROUGH). -->
      <div
        v-if="showSchematic && overlay"
        class="garment-preview__overlay"
        :style="overlayStyle"
        :data-testid="`garment-preview-overlay-${placementId}`"
        :data-overlay-w="overlay.wPct"
        :data-overlay-h="overlay.hPct"
      >
        <img
          v-if="artworkUrl"
          :src="artworkUrl"
          :alt="artworkAlt"
          class="garment-preview__art"
        />
        <span v-else class="garment-preview__art-fallback" aria-hidden="true">{{ artworkInitial }}</span>
      </div>

      <!-- BLOCKED state: the garment has no such zone. -->
      <div
        v-if="showSchematic && isBlocked"
        class="garment-preview__blocked"
        data-testid="garment-preview-blocked"
      >
        <span class="tf-icon garment-preview__blocked-icon">block</span>
        <span class="garment-preview__blocked-copy">{{ blockedCopy }}</span>
      </div>

      <!-- ROUGH badge: signals the preview is approximate (not for BLOCKED). -->
      <span
        v-if="showSchematic && isRough"
        class="garment-preview__rough-badge"
        data-testid="garment-preview-rough-badge"
        title="Approximate preview -- the fit verdict is still exact."
      >Approximate</span>

      <!-- Loading affordance (card oSOHJfg0): while the real-render layer is
           resolving a preview (checking the shared cache + possibly calling
           Artifi), overlay an animated shimmer on the schematic placeholder so
           the operator sees a "loading" cue instead of the schematic sitting
           silently then snapping to the render ~5s later. The schematic (or the
           previous color's render) stays visible UNDERNEATH -- never a blank
           box. Cleared the moment the render arrives (renderPending -> false)
           and the render fades in. Re-appears on a color switch (the render
           layer re-emits loading=true for the new cache key). Respects
           prefers-reduced-motion (static dim, no sweep). -->
      <div
        v-if="canRealRender && renderPending"
        class="garment-preview__loading"
        :data-testid="`garment-preview-loading-${placementId}`"
        aria-hidden="true"
      >
        <span class="garment-preview__shimmer" />
      </div>
    </div>

    <!-- ADR 0065 AMENDMENT: operator-color control. A placement is not
         color-specific but Artifi renders per SKU+color+view, so the operator
         picks the color they want to preview (mirrors the product-editor Stage
         precedent). Defaults to the first RENDERABLE color; unsupported colors
         are dimmed (not hidden). Shown only when the product is Artifi-eligible
         and offers a color set. -->
    <div
      v-if="canRealRender && colorSkus.length"
      class="garment-preview__colors"
      :data-testid="`garment-preview-color-control-${placementId}`"
    >
      <button
        v-for="c in colorSkus"
        :key="c"
        type="button"
        class="garment-preview__color-chip"
        :class="{
          'garment-preview__color-chip--active': c === effectiveColor,
          'garment-preview__color-chip--dimmed': isUnsupported(c),
        }"
        :data-testid="`garment-preview-color-chip-${placementId}-${c}`"
        :aria-pressed="c === effectiveColor"
        :title="isUnsupported(c) ? c + ' -- no live preview available' : c"
        @click="selectColor(c)"
      >
        {{ c }}
      </button>
    </div>

    <!-- GH #2874: the composite render DROPS a placement it cannot draw (most
         often two decorations resolving to the same garment view) and reports
         why in the response's `warnings` array. Nothing read that array
         before, so a preview silently missing a decoration looked identical to
         a correct one -- reported as decorations "randomly moving".

         Sits directly under the stage so it reads as a caption on the image it
         qualifies. NOT gated on hasRealRender: the warning describes what the
         BACKEND dropped from the composite, which stays true whether the
         operator is looking at the real render or the schematic fallback.

         role="status" is deliberate (informational, not an error the operator
         caused) -- see the a11y note on `previewWarning` below. -->
    <div
      v-if="previewWarning"
      class="garment-preview__warning"
      :class="{ 'garment-preview__warning--bare': previewWarning.severity === 'bare' }"
      :data-testid="`garment-preview-warning-${placementId}`"
      :data-severity="previewWarning.severity"
      role="status"
    >
      <span class="tf-icon garment-preview__warning-icon" aria-hidden="true">info</span>
      <span class="garment-preview__warning-copy">{{ previewWarning.message }}</span>
    </div>

    <div class="garment-preview__meta">
      <div class="garment-preview__label" data-testid="garment-preview-label">
        <!-- Jake (2026-08-23): the product name is a link to that
             product's editor. Falls back to plain text when the operator
             cannot reach the editor -- see `productLinkable`. -->
        <router-link
          v-if="productLinkable"
          :to="productEditorRoute"
          class="garment-preview__product garment-preview__product--link"
          :data-testid="`garment-preview-product-link-${placementId}`"
        >{{ productName }}</router-link>
        <span
          v-else
          class="garment-preview__product"
          :data-testid="`garment-preview-product-${placementId}`"
        >{{ productName }}</span>
        <span class="garment-preview__zone">{{ zoneLabel }}</span>
        <span v-if="dimsLabel" class="garment-preview__dims">{{ dimsLabel }}</span>
      </div>
      <!-- No data-testid here: PlacementCompatibilityIndicator sets its OWN
           per-verdict root testid (placement-compat-fit / -remap / -drop /
           -blocked); a static data-testid on the component would override it. -->
      <placement-compatibility-indicator
        :verdict="verdict.verdict"
        :reason="verdict.reason"
        :size_delta_pct="verdict.size_delta_pct"
      />
    </div>
  </div>
</template>

<script>
import { computed, ref, toRef } from "vue";
// Imported explicitly rather than relying on the router's global
// registration: this component is mounted directly by several specs
// that install no router, and an unresolved <router-link> renders
// nothing while only warning to the console.
import { RouterLink } from "vue-router";
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import PlacementCompatibilityIndicator from "@/components/decoration/PlacementCompatibilityIndicator.vue";
import GarmentRenderLayer from "@/components/decoration/GarmentRenderLayer.vue";
import { describePreviewWarnings } from "@/components/decoration/previewWarningCopy";
import { useArtifiCoverage } from "@/composables/useArtifiCoverage";
import { artifiPreviewEligible } from "@/utils/supplier-entitlement";
import { decorationRowImageSrc } from "@/utils/artworkPreviewState";
import {
  computePlacementVerdict,
  zoneInchesFor,
  hotspotFor,
  overlayUnitsFor,
  viewBoxFor,
} from "@/components/decoration/placementPreviewGeometry";

const APPAREL_VIEWBOX = [120, 140];

// The stage's own aspect ratio -- MUST match `.garment-preview__stage`'s
// `aspect-ratio: 6 / 7` in the scoped CSS below. Used to reconcile overlay
// coordinates with GarmentSilhouette's contain-fit letterbox (a cap viewBox
// 140x100 letterboxes hard against this 6/7 stage).
const STAGE_ASPECT = 6 / 7;

// The inner pad GarmentSilhouette insets its art by on all sides -- MUST match
// GarmentSilhouette.vue's `pad` prop default ("16%"). We render it with the
// default pad, so the garment art occupies the stage minus this pad, then
// contain-fits its viewBox inside that padded box (letterboxing the axis whose
// aspect does not match).
const SILHOUETTE_PAD = 0.16;

/**
 * The garment-art placement geometry inside the stage, mirroring how
 * GarmentSilhouette renders: inset by SILHOUETTE_PAD on all sides, then the
 * viewBox contain-fit + centered inside that padded box. Returns an origin +
 * drawn art size, all as fractions of the stage on their respective axes
 * (x/width relative to stage width, y/height relative to stage height), so
 * `mapPoint` / `mapLength` below convert viewBox units straight to the
 * percentages the overlay's `left/top/width/height` use.
 *
 * @param {[number, number]} viewBox  [vbW, vbH] in viewBox units
 * @returns {{vbW, vbH, aw, ah, xOrigin, yOrigin, sw, sh}}
 */
function silhouetteArtGeometry([vbW, vbH]) {
  // Normalize the stage to height 1, width = aspect. Percentages of stage
  // width/height fall out directly by dividing by sw / sh at the end.
  const sw = STAGE_ASPECT;
  const sh = 1;
  const pw = (1 - 2 * SILHOUETTE_PAD) * sw; // padded box width
  const ph = (1 - 2 * SILHOUETTE_PAD) * sh; // padded box height
  // contain-fit: the largest uniform scale that fits the viewBox in the padded
  // box; the non-binding axis letterboxes (centered).
  const s = Math.min(pw / vbW, ph / vbH);
  const aw = s * vbW; // drawn art width  (stage-normalized)
  const ah = s * vbH; // drawn art height (stage-normalized)
  const xOrigin = SILHOUETTE_PAD * sw + (pw - aw) / 2; // pad + horizontal letterbox
  const yOrigin = SILHOUETTE_PAD * sh + (ph - ah) / 2; // pad + vertical letterbox
  return { vbW, vbH, aw, ah, xOrigin, yOrigin, sw, sh };
}

export default {
  name: "GarmentPreview",

  components: {
    RouterLink,
    "router-link": RouterLink,
    "garment-silhouette": GarmentSilhouette,
    GarmentSilhouette,
    "placement-compatibility-indicator": PlacementCompatibilityIndicator,
    PlacementCompatibilityIndicator,
    "garment-render-layer": GarmentRenderLayer,
    GarmentRenderLayer,
  },

  props: {
    // Decoration -- carries width_in / height_in and (best-effort) art.
    decoration: {
      type: Object,
      required: true,
    },
    // Placement -- carries placement_zone {slug, display_name, default_max_*},
    // product_name, (best-effort) product_silhouette / size_scale_factor, and
    // (ADR 0065 amendment) supplier_sku / supplier_name / color_skus /
    // preview_color for the real Artifi render.
    placement: {
      type: Object,
      required: true,
    },
  },

  emits: ["preview-kind"],

  inject: {
    distributorLayoutCapabilities: { default: () => [] },
  },

  // ADR 0065 amendment: the operator-color state + coverage-driven default
  // (first RENDERABLE color) live in setup() so we can reuse the Stage's
  // useArtifiCoverage. Kept alongside the Options API (Vue 3 supports both).
  setup(props) {
    const placementRef = toRef(props, "placement");

    const supplierSku = computed(() => placementRef.value?.supplier_sku || null);
    const supplierName = computed(
      () => placementRef.value?.supplier_name || null,
    );
    const supplierKey = computed(() => placementRef.value?.supplier_key ?? null);
    const supplierId = computed(() => placementRef.value?.supplier_id ?? null);
    // Only suppliers whose styles Artifi carries (SanMar, S&S) have coverage
    // (mirrors Stage.artifiSku, artifiPreviewEligible). A non-eligible product
    // feeds a null SKU so the coverage watcher never fires -- and the
    // real-render layer is never mounted.
    const isArtifiEligible = computed(() =>
      artifiPreviewEligible({
        supplierKey: supplierKey.value,
        supplierKeyResolved: supplierId.value != null,
        supplierName: supplierName.value,
      }),
    );
    const artifiSku = computed(() =>
      isArtifiEligible.value ? supplierSku.value : null,
    );
    const colorSkus = computed(() => {
      const raw = placementRef.value?.color_skus;
      return Array.isArray(raw) ? raw.filter((c) => typeof c === "string") : [];
    });

    // Coverage pre-check -- also feeds the default first-RENDERABLE color and
    // dims unsupported chips. Shared (module-cached) with the render layer's
    // own useArtifiCoverage(artifiSku) call, so this is one request per SKU.
    const { isUnsupported } = useArtifiCoverage(artifiSku);

    // The operator-selected color. Defaults to the placement's preview_color
    // (a starting point) but resolves to the FIRST RENDERABLE color once
    // coverage lands (Jake's decision #1 -- never render selected_colors[0]
    // blind). Null until a color is available.
    const selectedColor = ref(null);

    // The default renderable color: the first color coverage does NOT mark
    // unsupported, else the placement's preview_color, else the first color.
    const defaultRenderableColor = computed(() => {
      const colors = colorSkus.value;
      if (!colors.length) return placementRef.value?.preview_color || null;
      const renderable = colors.find((c) => !isUnsupported(c));
      return (
        renderable || placementRef.value?.preview_color || colors[0] || null
      );
    });

    const hasRealRender = ref(false);
    // Whether the real-render layer is currently RESOLVING a preview (cache
    // check + possibly an Artifi call). Driven by the layer's `loading` emit;
    // gates the loading shimmer. Starts false and re-goes true on a color
    // switch (the layer re-resolves for the new cache key). Card oSOHJfg0.
    const renderPending = ref(false);
    // Raw warning codes from the last resolved preview, forwarded by the
    // render layer (GH #2874). Kept RAW here -- translation to human copy is
    // previewWarningCopy's single job, applied in the `previewWarning`
    // computed below, so the mapping never exists in two places.
    const renderWarnings = ref([]);

    return {
      supplierSku,
      supplierName,
      isArtifiEligible,
      supplierKey,
      supplierId,
      colorSkus,
      isUnsupported,
      selectedColor,
      defaultRenderableColor,
      hasRealRender,
      renderPending,
      renderWarnings,
    };
  },

  computed: {
    // Capabilities via the DistributorLayout injection (the pattern used
    // by ClientDepartmentsPanel, DecorationLibraryArtworkDetail and the
    // other distributor surfaces). Injection rather than the Pinia-backed
    // usePermissions composable because this component is mounted directly
    // by specs that install no Pinia, and `inject` has a safe [] default
    // where getActivePinia() throws.
    capabilities() {
      const inj = this.distributorLayoutCapabilities;
      if (Array.isArray(inj)) return inj;
      if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
      }
      return [];
    },

    // The product_editor route is guarded by
    // requireCapability('product.edit', '/admin') (router/index.js), so a
    // caller without it is redirected OUT of the store editor rather than
    // landing on the editor. Without the capability the name stays the
    // plain text it was.
    canEditProduct() {
      return this.capabilities.includes("product.edit");
    },

    placementId() {
      return this.placement?.placement_id ?? "x";
    },

    // The campaign product this preview's placement sits on. The rollup
    // (decoration_view.for_store) emits it per placement; it is the
    // :campaignProductId param of the product_editor route, NOT a catalog
    // product id.
    campaignProductId() {
      return this.placement?.campaign_product_id ?? null;
    },

    // Link only when we can build a route that actually resolves: the
    // capability must be held AND we must have an id. Without the id the
    // route would render /product-editor/undefined -- a 404 dead-end,
    // which is worse than plain text.
    productLinkable() {
      return this.canEditProduct && this.campaignProductId != null;
    },

    productEditorRoute() {
      return {
        name: "product_editor",
        params: { campaignProductId: this.campaignProductId },
      };
    },

    zone() {
      return this.placement?.placement_zone || null;
    },

    zoneSlug() {
      return this.zone?.slug || null;
    },

    zoneLabel() {
      return this.zone?.display_name || this.zoneSlug || "Unzoned";
    },

    productName() {
      return this.placement?.product_name || "Product";
    },

    // Garment class. The rollup (decoration_view.for_store) now emits a
    // resolved `product_silhouette` per placement -- derived backend-side from
    // product_name + product_type_name via the shared services/silhouette
    // helper (the SAME derivation the storefront tinted placeholder uses),
    // whose frontend mirror is @/utils/silhouetteFor. A known garment class
    // (tee/polo/hoodie/...) drives the TRUE render path (real zone hotspot +
    // true-scale overlay); "generic" (or an absent field) falls back to the
    // rough centered preview. Reading the resolved class straight off the placement
    // keeps this surface a pass-through -- no parallel mapper here.
    resolvedSilhouette() {
      const s = this.placement?.product_silhouette;
      return typeof s === "string" && s.length > 0 ? s : "generic";
    },

    // Youth-scale factor (ADR 0065). Best-effort off the placement; NULL/absent
    // means adult (1.0), matching the backend.
    sizeScaleFactor() {
      const s = this.placement?.size_scale_factor;
      return typeof s === "number" ? s : 1.0;
    },

    widthIn() {
      const w = this.decoration?.width_in;
      return typeof w === "number" ? w : null;
    },
    heightIn() {
      const h = this.decoration?.height_in;
      return typeof h === "number" ? h : null;
    },

    zoneInches() {
      return zoneInchesFor(this.zone);
    },

    // The authored hotspot for this (silhouette, zone). Null -> rough/blocked.
    hotspot() {
      if (!this.zoneSlug) return null;
      return hotspotFor(this.resolvedSilhouette, this.zoneSlug);
    },

    // Does the garment offer this zone at all? The silhouette's authored zones
    // (across every face) are the offer set, and hotspotFor already resolves the
    // face, so a null hotspot on a NON-generic silhouette means the zone is
    // genuinely not offered -> blocked. The generic silhouette offers none, but
    // that is the ROUGH fallback (ADR 0065), not a blocked zone.
    isBlocked() {
      if (this.resolvedSilhouette === "generic") return false;
      return this.hotspot === null;
    },

    isTrue() {
      return (
        this.resolvedSilhouette !== "generic" &&
        this.hotspot !== null &&
        this.zoneInches !== null
      );
    },

    isRough() {
      // Rough = we render a silhouette + a schematic mark but not a true-scale
      // hotspot overlay: generic silhouette, or no dims. NOT blocked.
      return !this.isTrue && !this.isBlocked;
    },

    // The self-reported preview quality, emitted for coverage logging.
    previewKind() {
      if (this.isBlocked) return "blocked";
      if (this.isTrue) return "true";
      if (this.zoneInches === null) return "no-dims";
      return "generic";
    },

    verdict() {
      // The verdict is EXACT regardless of preview quality (arithmetic on
      // inches). A blocked zone has no fit question -> neutral pill.
      if (this.isBlocked) {
        return {
          verdict: "blocked",
          reason: `${this.productName} has no ${this.zoneLabel} placement zone.`,
          size_delta_pct: null,
        };
      }
      const [zw, zh] = this.zoneInches || [null, null];
      return computePlacementVerdict({
        widthIn: this.widthIn,
        heightIn: this.heightIn,
        zoneWidthIn: zw,
        zoneHeightIn: zh,
        sizeScaleFactor: this.sizeScaleFactor,
      });
    },

    viewBox() {
      return viewBoxFor(this.resolvedSilhouette) || APPAREL_VIEWBOX;
    },

    face() {
      return this.hotspot?.face || "front";
    },

    // The garment-art placement geometry inside the stage, mirroring
    // GarmentSilhouette's 16% pad + contain-fit + centering (see
    // silhouetteArtGeometry). The overlay maps its viewBox coordinates through
    // this so it lands ON the actual art (not floating in the pad / cap
    // letterbox whitespace).
    artGeometry() {
      return silhouetteArtGeometry(this.viewBox);
    },

    // The overlay geometry in the silhouette's viewBox units (cx, cy = box
    // CENTRE; w, h = box size). TRUE uses the hotspot + true-scale math; ROUGH
    // centers a fixed schematic mark. Null when blocked. The viewBox->stage-%
    // reconciliation happens in `overlay` via `mapBoxToStage`.
    overlayUnits() {
      if (this.isBlocked) return null;
      const [vbW, vbH] = this.viewBox;
      if (this.isTrue) {
        const [zw, zh] = this.zoneInches;
        return overlayUnitsFor({
          hot: this.hotspot,
          zoneWidthIn: zw,
          zoneHeightIn: zh,
          widthIn: this.widthIn,
          heightIn: this.heightIn,
          sizeScaleFactor: this.sizeScaleFactor,
        });
      }
      // ROUGH: a centered schematic mark sized to a modest fraction of the
      // garment. It does NOT claim true scale (hence the "Approximate" badge).
      const w = vbW * 0.28;
      const h = vbH * 0.2;
      return { cx: vbW / 2, cy: vbH * 0.42, w, h };
    },

    // Map a centred viewBox box {cx, cy, w, h} into stage percentages, applying
    // the pad + contain-fit + letterbox reconciliation. The overlay mark scales
    // truly RELATIVE to its zone box because the SAME contain scale + axis
    // divisor apply to both -- this reconciles POSITION only, preserving the
    // true-relative-scale the 3b geometry established.
    overlay() {
      const u = this.overlayUnits;
      if (!u) return null;
      return this.mapBoxToStage(u.cx - u.w / 2, u.cy - u.h / 2, u.w, u.h);
    },

    overlayStyle() {
      if (!this.overlay) return {};
      return {
        left: `${this.overlay.leftPct}%`,
        top: `${this.overlay.topPct}%`,
        width: `${this.overlay.wPct}%`,
        height: `${this.overlay.hPct}%`,
      };
    },

    silhouetteColor() {
      // A neutral light garment so the (usually dark) mark reads. The real
      // per-color swatch is a later enrichment; the schematic does not need it.
      return { name: "", hex: "#eef2f6" };
    },

    // The shared tile-image rule (the Decorations row's): a stitch file's
    // READY preview, else the thumbnail / image_url -- never a raw .dst.
    artworkUrl() {
      return decorationRowImageSrc(this.decoration);
    },

    artworkAlt() {
      return `${this.productName} ${this.zoneLabel} preview`;
    },

    artworkInitial() {
      const name =
        this.decoration?.artwork_original_filename ||
        this.decoration?.artwork_filename ||
        this.productName ||
        "?";
      const ch = String(name).trim()[0];
      return ch ? ch.toUpperCase() : "?";
    },

    dimsLabel() {
      if (this.widthIn == null || this.heightIn == null) return "";
      return `${this.widthIn} x ${this.heightIn} in`;
    },

    blockedCopy() {
      return `No ${this.zoneLabel} zone on this garment -- can't preview`;
    },

    // GH #2874 -- the preview-warning indication. Turns the raw codes the
    // render layer forwarded into one human sentence plus a severity, or null
    // when there is nothing to tell the operator.
    //
    // Null is the healthy case and must stay cheap: describePreviewWarnings
    // returns null for an empty array AND for an array holding only
    // non-user-facing codes (e.g. "stale", where the image IS the right
    // decoration and only came from cache). A badge on a healthy preview
    // trains operators to ignore the badge on the previews that matter.
    //
    // A11Y: the template renders this with role="status", NOT role="alert".
    // The distinction is deliberate -- a dropped placement is a limitation of
    // the garment's available views, not an error the operator caused or must
    // act on, so a screen reader should mention it in turn rather than
    // interrupt with a failure.
    previewWarning() {
      return describePreviewWarnings(this.renderWarnings);
    },

    // ADR 0065 amendment: real-Artifi-render wiring. -------------------------

    // The color the operator is currently looking at: their selection, else
    // the coverage-driven first-RENDERABLE default. Drives the render.
    effectiveColor() {
      return this.selectedColor || this.defaultRenderableColor;
    },

    // Whether to even MOUNT the real-render layer. Only for a Artifi-eligible
    // product with a resolvable zone + color -- everything else is schematic
    // only (the render layer never fires). The layer itself applies the six
    // never-unbounded-3P controls and reports back via has-render.
    canRealRender() {
      return Boolean(
        this.isArtifiEligible &&
          this.supplierSku &&
          this.zoneSlug &&
          this.effectiveColor,
      );
    },

    // Schematic shows when there is NO real render on screen: either the layer
    // isn't eligible, or it reported has-render=false (over budget / color
    // unsupported / no zone box / render failed). This is the fallback contract
    // -- the schematic (3b) is retained, never deleted.
    showSchematic() {
      return !(this.canRealRender && this.hasRealRender);
    },

    // The zone slug the render layer resolves to an Artifi view.
    zoneSlug() {
      return this.zone?.slug ?? null;
    },

    // The artwork bound to this placement (best-effort, off the decoration).
    // A raster/uploaded artwork id string; null -> bare-garment render.
    renderArtworkFileId() {
      const d = this.decoration || {};
      const id = d.artwork_file_id ?? d.artwork_id ?? null;
      return id != null ? String(id) : null;
    },
  },

  watch: {
    // Keep the operator selection valid: initialize it to the default
    // renderable color, and re-default when the product's color set changes.
    defaultRenderableColor: {
      immediate: true,
      handler(next) {
        if (!this.selectedColor && next) {
          this.selectedColor = next;
        }
      },
    },
  },

  methods: {
    // ADR 0065 amendment: operator-color control + render-availability hook.
    selectColor(color) {
      if (typeof color === "string" && color) {
        this.selectedColor = color;
      }
    },

    onHasRender(has) {
      this.hasRealRender = Boolean(has);
    },

    // The real-render layer reports whether it is RESOLVING a preview (cache
    // check + possibly an Artifi call). Drives the loading shimmer. Card
    // oSOHJfg0. When true, the shimmer overlays the schematic (or the previous
    // color's render) as a loading cue; when false, the shimmer clears and --
    // if a render arrived -- it fades in.
    onLoading(pending) {
      this.renderPending = Boolean(pending);
    },

    // The real-render layer forwards the warning codes the composite resolved
    // with (GH #2874). Stored raw; `previewWarning` turns them into copy.
    // Always assigns (never merges) so a clean re-render CLEARS a stale
    // warning from the previous color rather than leaving it on screen.
    onWarnings(codes) {
      this.renderWarnings = Array.isArray(codes) ? codes : [];
    },

    // Map a viewBox box (top-left ux, uy; size uw, uh -- all in viewBox units)
    // to stage percentages, using the silhouette art geometry (pad + contain
    // + letterbox). x/width divide by the stage width (aspect); y/height by the
    // stage height (1). Used by the overlay mark to stay co-centred on the
    // actual garment art relative to its zone.
    mapBoxToStage(ux, uy, uw, uh) {
      const g = this.artGeometry;
      return {
        leftPct: ((g.xOrigin + (ux / g.vbW) * g.aw) / g.sw) * 100,
        topPct: ((g.yOrigin + (uy / g.vbH) * g.ah) / g.sh) * 100,
        wPct: (((uw / g.vbW) * g.aw) / g.sw) * 100,
        hPct: (((uh / g.vbH) * g.ah) / g.sh) * 100,
      };
    },
  },

  mounted() {
    // Report the preview quality so the parent can build a loud coverage log
    // (which placements got a true vs rough/generic/blocked preview).
    this.$emit("preview-kind", {
      placement_id: this.placementId,
      product_name: this.productName,
      zone: this.zoneSlug,
      silhouette: this.resolvedSilhouette,
      kind: this.previewKind,
    });
  },
};
</script>

<style scoped>
.garment-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 168px;
}

.garment-preview__stage {
  position: relative;
  width: 168px;
  aspect-ratio: 6 / 7;
  border: 1px solid var(--tf-border, #e2e8f0);
  border-radius: 8px;
  background: #f5f8fa;
  overflow: hidden;
}

.garment-preview__silhouette {
  position: absolute;
  inset: 0;
}

.garment-preview__overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.garment-preview__art {
  max-width: 100%;
  max-height: 100%;
  /* Contain-fit, never crop or stretch (ADR 0062). */
  object-fit: contain;
}

.garment-preview__art-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(19, 55, 82, 0.82);
  color: #fff;
  border-radius: 3px;
  font-weight: 700;
  font-size: 13px;
}

.garment-preview__blocked {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 12%;
  text-align: center;
  background: rgba(255, 255, 255, 0.86);
}

.garment-preview__blocked-icon {
  color: #991b1b;
  font-size: 22px;
}

.garment-preview__blocked-copy {
  color: #991b1b;
  font-size: 11px;
  line-height: 1.4;
  font-weight: 500;
}

/* GH #2874 -- preview warning caption. Sits under the stage as a caption on
   the image it qualifies. Informational styling by default (quiet, neutral);
   the --bare modifier warms it because that state (a plain garment served in
   place of a decorated preview) is the one most easily mistaken for a correct
   preview. Neither variant uses error red: nothing has failed and the operator
   did nothing wrong. */
.garment-preview__warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid var(--tf-border, #e2e8f0);
  background: var(--tf-surface-muted, #f5f7fa);
  color: var(--tf-text-muted, #52606d);
  font-size: 11px;
  line-height: 1.45;
}

.garment-preview__warning--bare {
  border-color: var(--tf-warning, #fcb900);
  background: var(--tf-warning-soft, #fff8e1);
  color: var(--tf-warning-dark, #8a6d3b);
}

.garment-preview__warning-icon {
  flex: 0 0 auto;
  font-size: 13px;
  line-height: 1.25;
}

.garment-preview__warning-copy {
  flex: 1 1 auto;
}

.garment-preview__rough-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(19, 55, 82, 0.86);
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* Real-render layer wrapper -- fades the Artifi render in over the schematic
   placeholder instead of an instant swap (card oSOHJfg0). Starts transparent;
   the --faded-in modifier (applied when has-render lands) transitions opacity
   to 1 over ~200ms. prefers-reduced-motion users get the render immediately
   (no transition). */
.garment-preview__render {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 200ms ease-in;
}

.garment-preview__render--faded-in {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .garment-preview__render {
    transition: none;
  }
}

/* Loading affordance -- an animated shimmer sweep over the schematic while a
   preview resolves (card oSOHJfg0). A translucent gradient band travels across
   the stage, reading as a content-loading placeholder (not a jarring spinner).
   Sits above the schematic layers but never replaces them (the placeholder is
   always visible underneath). Respects prefers-reduced-motion: the sweep is
   replaced by a static, non-animated dim so motion-sensitive users still get a
   subtle "loading" cue without movement. */
.garment-preview__loading {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.garment-preview__shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0) 30%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 70%
  );
  background-size: 220% 100%;
  animation: garment-preview-shimmer 1.25s ease-in-out infinite;
}

@keyframes garment-preview-shimmer {
  0% {
    background-position: 180% 0;
  }
  100% {
    background-position: -80% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .garment-preview__shimmer {
    animation: none;
    background: rgba(19, 55, 82, 0.06);
  }
}

.garment-preview__colors {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.garment-preview__color-chip {
  font-size: 10px;
  line-height: 1.2;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid var(--tf-border, #e2e8f0);
  background: #fff;
  color: var(--tf-text-muted, #52606d);
  cursor: pointer;
}

.garment-preview__color-chip--active {
  border-color: var(--tf-brand-primary, #119ae9);
  color: var(--tf-brand-primary, #119ae9);
  font-weight: 600;
}

.garment-preview__color-chip--dimmed {
  opacity: 0.4;
}

.garment-preview__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.garment-preview__label {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 12px;
  line-height: 1.35;
}

.garment-preview__product {
  font-weight: 600;
  color: var(--tf-text, #1f2933);
}

/* Linked form. Undecorated at rest so the caption reads the same as the
   plain-text form; the affordance appears on hover. */
.garment-preview__product--link {
  text-decoration: none;
  cursor: pointer;
}
.garment-preview__product--link:hover,
.garment-preview__product--link:focus-visible {
  color: var(--tf-primary, #119ae9);
  text-decoration: underline;
}

.garment-preview__zone {
  color: var(--tf-text-muted, #52606d);
}

.garment-preview__dims {
  color: var(--tf-text-muted, #7b8794);
  font-size: 11px;
}
</style>
